<template>
  <div :class="$style.rootWrapper">
    <HgPageNotFound v-if="hasParseError" :theme="theme" />
    <LayoutSolutionResultListPage v-if="searchModel" :searchModel="searchModel" />
  </div>
</template>

<script lang="ts">
import { getSSRContext } from '@/helpers/SSR/ssr';
import { SSRError } from '@/helpers/SSR/types';
import { HgSupportedLanguage } from '@smg-real-estate/domus';
import type { GeoLocation } from '@smg-real-estate/geo-libs';
import {
  parseSRPUri,
  SearchModel,
  type SRPUriSpecification,
} from '@smg-real-estate/hg-search-libs';
import pWaitFor from 'p-wait-for';
import { defineComponent } from 'vue';
import { mapGetters } from 'vuex';

import { getCurrentPlatformTheme, getCurrentTargetPlatform } from '@/helpers/platformDetection';
import LayoutSolutionResultListPage from '@/views/LayoutSolutionResultListPage.vue';
import HgPageNotFound from '@smg-real-estate/domus/HgPageNotFound.vue';

export default defineComponent({
  name: 'SeoEntryResultListPage',
  components: {
    HgPageNotFound,
    LayoutSolutionResultListPage,
  },
  props: {
    language: {
      required: true,
      type: String,
    },
    path: {
      required: true,
      type: String,
    },
    query: {
      type: Object,
      default: undefined,
    },
  },
  setup() {
    return {
      ssrContext: getSSRContext(),
    };
  },
  data: () => ({
    localSearchModel: undefined as undefined | SearchModel,
    theme: getCurrentPlatformTheme(),
  }),
  computed: {
    ...mapGetters('geo', {
      getGeoLocationsByUrlName: 'getGeoLocationsByUrlName',
      getGeoLocationsById: 'getGeoLocationsById',
      allGeoLocationsByUrlNameSettled: 'allGeoLocationsByUrlNameSettled',
      allGeoLocationsByIdSettled: 'allGeoLocationsByIdSettled',
    }),
    specification(): SRPUriSpecification | undefined | null {
      try {
        return parseSRPUri(this.path, this.query, false, getCurrentTargetPlatform());
      } catch (error) {
        return null;
      }
    },
    hasParseError(): boolean {
      return this.specification === null;
    },
    geoUrlNamesToResolve(): string[] {
      return this.specification?.locationUrlNames.map((name) => name.toLowerCase()) ?? [];
    },
    resolvedGeoLocationsFromUrlNames(): GeoLocation[] {
      const language = this.language as HgSupportedLanguage;

      return this.getGeoLocationsByUrlName(this.geoUrlNamesToResolve, language);
    },
    resolvedGeoLocationsFromIds(): GeoLocation[] {
      return this.getGeoLocationsById(this.geoIdsToResolveFromLoc);
    },
    isGeoUrlResolvePending(): boolean {
      const language = this.language as HgSupportedLanguage;

      return !this.allGeoLocationsByUrlNameSettled(this.geoUrlNamesToResolve, language);
    },
    geoIdsToResolveFromLoc(): string[] {
      return this.specification?.searchModel.locations ?? [];
    },
    isGeoIdResolveFromLocPending(): boolean {
      return !this.allGeoLocationsByIdSettled(this.geoIdsToResolveFromLoc);
    },
    locationIds(): string[] | undefined {
      const result: string[] = [];

      if (this.geoUrlNamesToResolve.length) {
        if (this.isGeoUrlResolvePending) {
          return undefined;
        }

        result.push(...this.resolvedGeoLocationsFromUrlNames.map((location) => location.id));
      }

      if (this.geoIdsToResolveFromLoc.length) {
        if (this.isGeoIdResolveFromLocPending) {
          return undefined;
        }
        // get the location ids from the found locations rather than the given ids
        result.push(...this.resolvedGeoLocationsFromIds.map((location) => location.id));
      }

      return result;
    },
    searchModel(): SearchModel | undefined {
      if (!this.specification || !this.locationIds) {
        return undefined;
      }

      return this.specification.searchModel.withLocations(this.locationIds);
    },
  },
  watch: {
    searchModel: {
      immediate: true,
      handler(searchModel) {
        // searchModel will be updated (touched) after any update to the geo module in the store. We should only
        // propagate and rerender the ResultListPage for real changes to the model
        if (
          searchModel &&
          !searchModel.isEqual(this.localSearchModel) &&
          !this.hasResolveErrors()
        ) {
          this.localSearchModel = searchModel;
        }
      },
    },
  },
  mounted() {
    this.triggerLocationsResolve();
  },
  methods: {
    hasResolveErrors(): boolean {
      if (this.geoUrlNamesToResolve.length) {
        return (
          !this.isGeoUrlResolvePending &&
          this.resolvedGeoLocationsFromUrlNames.length !== this.geoUrlNamesToResolve.length
        );
      }

      if (this.geoIdsToResolveFromLoc.length) {
        return (
          !this.isGeoIdResolveFromLocPending &&
          this.resolvedGeoLocationsFromIds.length !== this.geoIdsToResolveFromLoc.length
        );
      }

      return false;
    },
    triggerLocationsResolve() {
      const language = this.language as HgSupportedLanguage;

      if (this.geoUrlNamesToResolve.length) {
        this.$store.dispatch('geo/resolveLocationsByUrlName', {
          urlNames: this.geoUrlNamesToResolve,
          language,
        });
      }
      if (this.geoIdsToResolveFromLoc.length) {
        this.$store.dispatch('geo/resolveLocationsById', {
          geoLocationIds: this.geoIdsToResolveFromLoc,
        });
      }
    },
    renderSoft404() {
      if (this.ssrContext) {
        this.ssrContext.error = SSRError.PageNotFound;
      }
    },
  },
  async serverPrefetch() {
    if (this.hasParseError) {
      this.renderSoft404();
      return;
    }

    this.triggerLocationsResolve();

    try {
      await pWaitFor(() => !!this.searchModel, { timeout: { milliseconds: 16000 } });

      if (this.hasResolveErrors()) {
        this.renderSoft404();
      } else {
        this.localSearchModel = this.searchModel;
      }
    } catch (error) {
      this.ssrContext.error = SSRError.OtherError; // will send HTTP 500
    }
  },
});
</script>

<style module lang="scss">
.rootWrapper {
  padding: 0;
  margin: 0;
}
</style>
